import { isFsaPlanType, isLifeDisabilityType } from "../schemas/planTypes";
import { isSpouseRelationship } from "./enrollmentCalculator";

/**
 * Single source of truth for whether a plan covers dependents.
 *
 * Plans carry two flags — `spouseEligible` and `childrenEligible` (see the
 * `plans` table). These gate whether spouse/domestic-partner and child
 * dependents can be enrolled. Used by the enrollment UI (to show/hide the
 * dependent selector) and the server enrollment guard (to reject ineligible
 * dependents). Both must agree, so the logic lives here.
 */

/** Plan types that cover the employee only — dependents off by default. */
function isEmployeeOnlyPlanType(planType: string | undefined | null): boolean {
  return isLifeDisabilityType(planType) || isFsaPlanType(planType);
}

/**
 * Default dependent-eligibility flags for a newly created plan. Employee-only
 * products (Life, AD&D, Disability, FSA, Transit) start with dependents off;
 * everything else (Medical, Dental, Vision, …) starts on. Brokers can override
 * per plan on the Eligibility tab.
 */
export function defaultDependentEligibility(planType: string | undefined | null): {
  spouseEligible: boolean;
  childrenEligible: boolean;
} {
  const off = isEmployeeOnlyPlanType(planType);
  return { spouseEligible: !off, childrenEligible: !off };
}

/**
 * Whether a specific dependent may be enrolled under a plan, based on the
 * plan's eligibility flags and the dependent's relationship. FSA plans are
 * always employee-only (IRS). Flags are treated as eligible unless explicitly
 * `false`, so legacy plans with null flags keep covering dependents.
 */
export function isDependentEligibleForPlan(
  plan: { type?: string | null; spouseEligible?: boolean | null; childrenEligible?: boolean | null } | null | undefined,
  dependent: { relationship?: string | null } | null | undefined,
): boolean {
  if (!plan || !dependent) return false;
  if (isFsaPlanType(String(plan.type || ""))) return false;
  const rel = dependent.relationship;
  if (isSpouseRelationship(rel)) return plan.spouseEligible !== false;
  // Children and any other relationship are gated by the children flag.
  return plan.childrenEligible !== false;
}

/** The subset of `dependents` that may be enrolled under `plan`. */
export function eligibleDependentsForPlan<T extends { relationship?: string | null }>(
  plan: { type?: string | null; spouseEligible?: boolean | null; childrenEligible?: boolean | null } | null | undefined,
  dependents: T[] | null | undefined,
): T[] {
  return (dependents || []).filter((d) => isDependentEligibleForPlan(plan, d));
}

/** Whether the plan covers any dependents at all (drives showing the selector). */
export function planAllowsAnyDependents(
  plan: { type?: string | null; spouseEligible?: boolean | null; childrenEligible?: boolean | null } | null | undefined,
): boolean {
  if (!plan) return false;
  if (isFsaPlanType(String(plan.type || ""))) return false;
  return plan.spouseEligible !== false || plan.childrenEligible !== false;
}
